import { effectiveTerminals } from "./effective";
import type { ComponentDefinition, ComponentNode } from "./types";

// Victron SmartShunt/IP65 technical data §11.1 specifies the supplied 1 A
// slow-blow Vbatt lead. This is manufacturer evidence, not terminal ampacity.
// https://www.victronenergy.com/media/pg/SmartShunt/en/technical-data.html
// https://www.victronenergy.com/media/pg/SmartShunt_IP65/en/technical-data.html
// Victron states < 1 mA, without a typical value. The live ledger uses this
// upper bound to budget power; it is not a measured operating current.
export const SMARTSHUNT_SUPPLY = { fuseA: 1, minimumV: 6.5, maximumV: 70, drawUpperBoundA: 0.001 } as const;

const SMARTSHUNTS: Record<string, { model: string; ratingA: number }> = {
  "mon-smartshunt-300": { model: "SmartShunt 300A", ratingA: 300 },
  "mon-smartshunt-500": { model: "SmartShunt 500A/50mV (IP21)", ratingA: 500 },
  "mon-smartshunt-1000": { model: "SmartShunt 1000A", ratingA: 1000 },
  "mon-smartshunt-2000": { model: "SmartShunt 2000A", ratingA: 2000 },
  "mon-smartshunt-ip65-500": { model: "SmartShunt IP65 500A", ratingA: 500 },
};

/** Retaining a catalog id on an edited custom part does not establish its
 * manufacturer identity. Layout/name edits preserve the supported rule;
 * different hardware, terminal roles or internal wiring do not. */
export function isSupportedSmartShunt(def: ComponentDefinition, node: ComponentNode): boolean {
  if (!Object.hasOwn(SMARTSHUNTS, def.id)) return false;
  const profile = SMARTSHUNTS[def.id];
  if (def.category !== "monitor" || def.manufacturer !== "Victron" || def.model !== profile.model ||
      def.internalDcConnections?.length || def.oneWayDcConnections?.length || def.ampOutput != null) return false;
  const terms = effectiveTerminals(def, node);
  const matches = (id: string, polarity: string, direction: string, maxAmp: number) => {
    const matching = terms.filter(t => t.id === id);
    return matching.length === 1 && matching[0].polarity === polarity &&
      matching[0].direction === direction && matching[0].maxAmp === maxAmp;
  };
  return matches("batt-neg", "negative", "input", profile.ratingA) &&
    matches("load-neg", "negative", "output", profile.ratingA) && matches("vbat", "signal", "bidi", 1) &&
    terms.every(t => ["batt-neg", "load-neg", "vbat"].includes(t.id) ||
      (t.id === "vaux" && matches("vaux", "signal", "bidi", 1)) ||
      (t.id === "ve-direct" && matches("ve-direct", "signal", "bidi", 0)));
}

